import React, { useState, useEffect } from 'react';
import NoticeCard from '../Common/NoticeCard';
import { Notice } from '../../types/Notice';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

const PublicNotices: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'central' | 'building' | 'history'>('all');
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailNotice, setDetailNotice] = useState<Notice | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchNotices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, currentPage]);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filter !== 'all' && filter !== 'history') {
        params.append('type', filter);
      }
      params.append('page', currentPage.toString());
      params.append('per_page', '6');

      const endpoint = filter === 'history' ? '/notices/history' : '/notices';
      const response = await api.get(`${endpoint}?${params}`);
      
      if (response.data.success) {
        const payload = response.data.data;
        setNotices(payload.data || payload);
        setTotalPages(payload.last_page || 1);
      } else {
        setError(response.data.message || t('public.notices.fetchError'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('public.notices.fetchError'));
      console.error('Error fetching notices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter: 'all' | 'central' | 'building' | 'history') => {
    setFilter(newFilter);
    setShowAll(false);
    setCurrentPage(1);
  };

  const openDetail = async (noticeId: number) => {
    try {
      const res = await api.get(`/notices/${noticeId}`);
      if (res.data?.success) {
        setDetailNotice(res.data.data);
        setDetailOpen(true);
      }
    } catch (e) {
      // ignore
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailNotice(null);
  };

  if (loading) {
    return (
      <div className="public-notices-loading">
        <div className="loading-spinner"></div>
        <p>{t('public.notices.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-notices-error">
        <h3>{t('public.notices.errorTitle')}</h3>
        <p>{error}</p>
        <button 
          className="btn btn-primary"
          onClick={fetchNotices}
        >
          {t('public.notices.tryAgain')}
        </button>
      </div>
    );
  }

  const displayedNotices = notices;

  return (
    <div className="public-notices">
      <div className="notices-header">
        <h2>{t('public.notices.latest')}</h2>
        <div className="notices-filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            {t('public.notices.filters.all')}
          </button>
          <button
            className={`filter-btn ${filter === 'central' ? 'active' : ''}`}
            onClick={() => handleFilterChange('central')}
          >
            {t('public.notices.filters.central')}
          </button>
          <button
            className={`filter-btn ${filter === 'building' ? 'active' : ''}`}
            onClick={() => handleFilterChange('building')}
          >
            {t('public.notices.filters.building')}
          </button>
          {/* <button
            className={`filter-btn ${filter === 'history' ? 'active' : ''}`}
            onClick={() => handleFilterChange('history')}
          >
            Historical
          </button> */}
        </div>
      </div>

      {notices.length === 0 ? (
        <div className="no-notices">
          <div className="empty-state">
            <h3>{t('public.notices.emptyTitle')}</h3>
            <p>{t('public.notices.emptyDescription')}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="notices-grid">
            {displayedNotices.map((notice) => (
              <NoticeCard
                key={notice.id}
                notice={notice}
                showFullContent={false}
                onReadMore={() => openDetail(notice.id)}
                className="public-notice-card"
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="notices-actions pagination">
              <button
                className="btn btn-outline-primary"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ← Prev
              </button>
              <span className="page-indicator">Page {currentPage} of {totalPages}</span>
              <button
                className="btn btn-outline-primary"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {detailOpen && detailNotice && (
        <div className="notice-detail-modal">
          <div className="modal-backdrop" onClick={closeDetail}></div>
          <div className="modal-content notice-detail">
            <div className="modal-header">
              <h3>{detailNotice.title}</h3>
              <button className="close-btn" onClick={closeDetail}>×</button>
            </div>
            <div className="modal-body">
              <NoticeCard notice={detailNotice} showFullContent={true} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeDetail}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicNotices;
